import axios, {AxiosResponse} from 'axios';

import {
  CreateOpsCompanyMemberRequest,
  MoveToNetworkResult,
  MoveToNetworkRole,
  OpsCompanyMember,
  OpsCompanyStore,
  OpsMemberRemovalResult,
  OpsStoreOffers,
  PutStoreTeamRequest,
  StoreTeamMember,
  UpdateOpsCompanyMemberRequest,
} from '../models';

/**
 * Porta Ops do partner-hub-service (`docs/openapi.yaml` 1.2.0): lojas e pessoas
 * da conta do fornecedor e, desde a 1.7.0, as ofertas ativas das filiais do
 * fornecedor, com o token do funcionário. O PHS tem host próprio
 * (`partners.<ambiente>.mecanicity.mecanizou.com/v1`), fora do gateway do
 * `REACT_APP_API_URL`. Erros vêm com `code` no primeiro nível.
 */
const PARTNER_HUB_API_URL = process.env.REACT_APP_PARTNER_HUB_API_URL;

const companyUrl = (companyAccountUid: string) =>
  `${PARTNER_HUB_API_URL}/partner-hub/ops/companies/${companyAccountUid}`;

export const listOpsCompanyStores = (
  companyAccountUid: string
): Promise<OpsCompanyStore[]> =>
  axios
    .get(`${companyUrl(companyAccountUid)}/stores`)
    .then((d: AxiosResponse<OpsCompanyStore[]>) => d.data || []);

/** Desativar e reativar: a carteira de quem atende a loja fica. */
export const setOpsCompanyStoreDisabled = (
  companyAccountUid: string,
  providerStockUid: string,
  disabled: boolean
): Promise<OpsCompanyStore> =>
  axios
    .patch(`${companyUrl(companyAccountUid)}/stores/${providerStockUid}`, {
      disabled,
    })
    .then((d: AxiosResponse<OpsCompanyStore>) => d.data);

export const listOpsCompanyMembers = (
  companyAccountUid: string
): Promise<OpsCompanyMember[]> =>
  axios
    .get(`${companyUrl(companyAccountUid)}/members`)
    .then((d: AxiosResponse<OpsCompanyMember[]>) => d.data || []);

/**
 * Vendedor, Gerente ou Admin da rede (este com `stores: []`). Com quem já é da
 * rede, `ROLE_PROVIDER_ADMIN` promove. A pessoa vai pelo contato ou pelo
 * `user_uid` do cadastro escolhido no 409 `USER_AMBIGUOUS` (nunca os dois).
 */
export const createOpsCompanyMember = (
  companyAccountUid: string,
  body: CreateOpsCompanyMemberRequest
): Promise<OpsCompanyMember> =>
  axios
    .post(`${companyUrl(companyAccountUid)}/members`, body)
    .then((d: AxiosResponse<OpsCompanyMember>) => d.data);

export const updateOpsCompanyMember = (
  companyAccountUid: string,
  accountUserUid: string,
  body: UpdateOpsCompanyMemberRequest
): Promise<OpsCompanyMember> =>
  axios
    .patch(`${companyUrl(companyAccountUid)}/members/${accountUserUid}`, body)
    .then((d: AxiosResponse<OpsCompanyMember>) => d.data);

/** Repetir retoma os passos pendentes (`pending`). */
export const removeOpsCompanyMember = (
  companyAccountUid: string,
  accountUserUid: string
): Promise<OpsMemberRemovalResult> =>
  axios
    .delete(`${companyUrl(companyAccountUid)}/members/${accountUserUid}`)
    .then((d: AxiosResponse<OpsMemberRemovalResult>) => d.data);

// ---------------------------------------------------------------------------
// Quem atende a loja e equipe da conta própria da filial (1.3.0, §3.8)

/**
 * Define o conjunto de Vendedores e Gerentes que atendem a loja (o Admin da
 * rede não entra: tem todas). Responde a loja com as pessoas.
 */
export const setOpsStoreMembers = (
  companyAccountUid: string,
  providerStockUid: string,
  accountUserUids: string[]
): Promise<OpsCompanyStore> =>
  axios
    .put(
      `${companyUrl(companyAccountUid)}/stores/${providerStockUid}/members`,
      {accountUserUids}
    )
    .then((d: AxiosResponse<OpsCompanyStore>) => d.data);

const teamUrl = (companyAccountUid: string, providerStockUid: string) =>
  `${companyUrl(companyAccountUid)}/stores/${providerStockUid}/team`;

export const listOpsStoreTeam = (
  companyAccountUid: string,
  providerStockUid: string
): Promise<StoreTeamMember[]> =>
  axios
    .get(teamUrl(companyAccountUid, providerStockUid))
    .then((d: AxiosResponse<StoreTeamMember[]>) => d.data || []);

/**
 * Garante a pessoa na equipe da loja (idempotente), pelo contato ou pelo
 * `user_uid` do cadastro escolhido no 409 `USER_AMBIGUOUS`.
 */
export const putOpsStoreTeamMember = (
  companyAccountUid: string,
  providerStockUid: string,
  body: PutStoreTeamRequest
): Promise<StoreTeamMember> =>
  axios
    .put(teamUrl(companyAccountUid, providerStockUid), body)
    .then((d: AxiosResponse<StoreTeamMember>) => d.data);

/** Tira da equipe da loja (só o vínculo); repetir retoma o que ficou pendente. */
export const removeOpsStoreTeamMember = (
  companyAccountUid: string,
  providerStockUid: string,
  accountUserUid: string
): Promise<OpsMemberRemovalResult> =>
  axios
    .delete(`${teamUrl(companyAccountUid, providerStockUid)}/${accountUserUid}`)
    .then((d: AxiosResponse<OpsMemberRemovalResult>) => d.data);

/**
 * Passa a pessoa para a rede e a tira da equipe da loja: Vendedor ou Gerente
 * com a loja na carteira; Admin da rede (PHS 1.6.0) com todas. Quem já é da
 * rede mantém o papel de lá, menos no pedido de Admin, que promove.
 */
export const moveOpsStoreTeamMemberToNetwork = (
  companyAccountUid: string,
  providerStockUid: string,
  accountUserUid: string,
  role: MoveToNetworkRole
): Promise<MoveToNetworkResult> =>
  axios
    .post(
      `${teamUrl(
        companyAccountUid,
        providerStockUid
      )}/${accountUserUid}/move-to-network`,
      {role}
    )
    .then((d: AxiosResponse<MoveToNetworkResult>) => d.data);

// ---------------------------------------------------------------------------
// Ofertas ativas das filiais (1.7.0)

/**
 * Ofertas ativas de cada filial ativa do fornecedor no CRM, pelo `providerUid`
 * do CRM (não pede a conta do fornecedor: vale antes da ativação). O PHS
 * guarda a contagem por 12 h; `activeOffers` nulo = contagem indisponível.
 */
export const listOpsProviderStoreOffers = (
  providerUid: string
): Promise<OpsStoreOffers[]> =>
  axios
    .get(
      `${PARTNER_HUB_API_URL}/partner-hub/ops/providers/${providerUid}/stores/offers`
    )
    .then((d: AxiosResponse<{data: OpsStoreOffers[]}>) => d.data?.data || []);
